// biome-ignore-all lint/performance/noBarrelFile: Package entrypoint intentionally re-exports OpenCode runtime helpers.
import { bridgeAgentControllerConversationRef, buildAdapterCancelRequest, buildExecutableLaunchContract, createSharedControlShell, createSharedDispatchShell, resolveBuiltinAdapterConversationBridgeSpec, } from "@wakeloop/adapter-core";
import { resolveOpenCodeTimeoutMs as resolveOpenCodeTimeoutMsLocal, verifyOpenCodeLaunchContract as verifyOpenCodeLaunchContractLocal, } from "./config/opencode-runtime-config.js";
import { invokeOpenCodeWithSingleContextHeal as invokeOpenCodeWithSingleContextHealLocal } from "./invoke/opencode-invoke.js";
export { resolveInstalledOpenCodeLaunchCandidates, resolveInstalledOpenCodeLaunchContract, resolveOpenCodeTimeoutMs, verifyOpenCodeLaunchContract, } from "./config/opencode-runtime-config.js";
export { isLikelyOpenCodeContextLostError } from "./context/opencode-context-heal.js";
export { invokeOpenCodeWithSingleContextHeal, OpenCodeRuntimeError, } from "./invoke/opencode-invoke.js";
export { parseOpenCodeOutputDocument, parseOpenCodeOutputLine, } from "./output/opencode-output-parser.js";
export { invokeOpenCodeCliTransport, OpenCodeCliTransportError, } from "./transport/opencode-cli-transport.js";
const OPENCODE_CONVERSATION_BRIDGE_SPEC = resolveBuiltinAdapterConversationBridgeSpec("opencode");
export const createOpenCodeAdapter = (options) => {
    const controlShell = createSharedControlShell({
        gracefulTimeoutMs: options.gracefulTimeoutMs,
        sendCancel: options.sendCancel,
        sendInterrupt: options.sendInterrupt,
        waitForMs: options.waitForMs,
    });
    const dispatch = createSharedDispatchShell({
        adapterId: "opencode",
        bridgeSpec: OPENCODE_CONVERSATION_BRIDGE_SPEC,
        invoke: options.invoke,
        invokeByMode: options.invokeByMode,
        resolveDefaultInvoke: () => resolveDefaultOpenCodeInvoke(options),
        runtimeOptions: options,
    });
    return {
        adapterId: "opencode",
        cancel: controlShell.cancel,
        dispatch,
        interrupt: controlShell.interrupt,
    };
};
export const buildOpenCodeInterruptRequest = (input) => ({
    mode: "graceful",
    reason: input.reason,
    taskId: input.taskId,
});
export const buildOpenCodeCancelRequest = (input) => buildAdapterCancelRequest({
    reason: input.reason,
    taskId: input.taskId,
});
const resolveDefaultOpenCodeInvoke = (options) => {
    const openCodeTimeoutMs = options.openCodeTimeoutMs ?? resolveOpenCodeTimeoutMsLocal();
    return async (input) => {
        const launchContract = await resolveOpenCodeDispatchLaunchContract({
            inputLaunchContract: input.launchContract,
            openCodeBin: options.openCodeBin,
            optionLaunchContract: options.launchContract,
        });
        const openCodeWorkdir = input.runtimeContext.providerRuntimeWorkingDirectory;
        const onProcessControlEvent = resolveRuntimeProcessRegistrar({
            registerRuntimeProcess: options.registerRuntimeProcess,
            taskId: input.taskId,
        });
        const result = await invokeOpenCodeWithSingleContextHealLocal({
            launchContract,
            openCodeTimeoutMs,
            openCodeWorkdir,
            env: input.env,
            executionMode: input.executionMode,
            model: input.conversationPolicy?.model,
            ...(onProcessControlEvent ? { onProcessControlEvent } : {}),
            ...(input.onRuntimeEvent ? { onRuntimeEvent: input.onRuntimeEvent } : {}),
            prompt: input.prompt,
            sessionId: normalizeOptionalText(input.agentControllerConversationId),
        });
        const agentControllerConversation = OPENCODE_CONVERSATION_BRIDGE_SPEC
            ? bridgeAgentControllerConversationRef({
                bridgeSpec: OPENCODE_CONVERSATION_BRIDGE_SPEC,
                agentControllerConversationId: result.sessionId,
            })
            : null;
        return {
            contextDelta: {
                agentControllerConversationId: result.sessionId,
                refreshed: result.contextHealed,
                ...(agentControllerConversation ? { agentControllerConversation } : {}),
            },
            output: result.output,
            ...(result.telemetry ? { telemetry: result.telemetry } : {}),
        };
    };
};
const resolveOpenCodeDispatchLaunchContract = async (input) => {
    const launchContract = input.inputLaunchContract ??
        input.optionLaunchContract ??
        (input.openCodeBin
            ? buildExecutableLaunchContract({
                commandPath: input.openCodeBin,
            })
            : null);
    if (!launchContract) {
        throw Object.assign(new Error("OpenCode launch contract is required."), {
            code: "adapter.missing_launch_contract",
            errorType: "config",
        });
    }
    const verification = await verifyOpenCodeLaunchContractLocal({
        launchContract,
    });
    if (!verification.ok) {
        throw Object.assign(new Error(`OpenCode launch contract is not executable: ${verification.errorMessage}`), {
            code: "adapter.invalid_launch_contract",
            errorType: "config",
        });
    }
    return launchContract;
};
const resolveRuntimeProcessRegistrar = (input) => {
    if (!input.registerRuntimeProcess) {
        return undefined;
    }
    return (event) => {
        input.registerRuntimeProcess?.({
            ...event,
            taskId: input.taskId,
        });
    };
};
function normalizeOptionalText(value) {
    const normalized = String(value ?? "").trim();
    return normalized.length > 0 ? normalized : null;
}
